/**
 * AARKA Testimonial Marquee — Web Component
 * Lightweight infinite scroll with CSS-driven animation.
 * Clones cards for seamless looping, pauses off-screen for performance.
 * Works on both mobile and desktop.
 */

if (!customElements.get('aarka-testimonial-marquee')) {
  class AarkaTestimonialMarquee extends HTMLElement {
    constructor() {
      super();
      this.observer = null;
      this.resizeObserver = null;
    }

    connectedCallback() {
      // Small delay to ensure DOM is painted
      setTimeout(() => this.init(), 100);
    }

    init() {
      const speed = this.dataset.speed || 40;
      this.style.setProperty('--tm-speed', `${speed}s`);

      this.rows = this.querySelectorAll('.tm-row');

      // Clone cards for marquee on all screen sizes
      this.cloneCards();

      // Re-clone on resize crossing the 750px breakpoint
      this.mediaQuery = window.matchMedia('(min-width: 750px)');
      this.handleBreakpoint = (e) => {
        this.removeClones();
        this.cloneCards();
      };
      this.mediaQuery.addEventListener('change', this.handleBreakpoint);

      this.setupIntersectionObserver();
      this.setupTouchEvents();

      // Handle Shopify theme editor events
      if (Shopify && Shopify.designMode) {
        document.addEventListener('shopify:section:load', (e) => {
          if (e.target.contains(this)) {
            this.removeClones();
            this.cloneCards();
          }
        });

        document.addEventListener('shopify:block:select', (e) => {
          if (this.contains(e.target)) {
            this.rows.forEach(row => row.classList.add('tm-paused'));
          }
        });

        document.addEventListener('shopify:block:deselect', (e) => {
          if (this.contains(e.target)) {
            this.rows.forEach(row => row.classList.remove('tm-paused'));
          }
        });
      }
    }

    /**
     * Clone each row's children 2x so there are 3 copies total,
     * enabling a seamless infinite loop with the -33.333% translation.
     */
    cloneCards() {
      this.rows.forEach((row) => {
        // Avoid double-cloning
        if (row.dataset.cloned === 'true') return;

        const originals = Array.from(row.children);
        if (originals.length === 0) return;

        for (let copy = 0; copy < 2; copy++) {
          originals.forEach((card) => {
            const clone = card.cloneNode(true);
            clone.setAttribute('aria-hidden', 'true');
            clone.classList.add('tm-card--clone');
            row.appendChild(clone);
          });
        }

        row.dataset.cloned = 'true';
      });
    }

    /**
     * Remove cloned cards.
     */
    removeClones() {
      this.rows.forEach((row) => {
        if (row.dataset.cloned !== 'true') return;
        row.querySelectorAll('.tm-card--clone').forEach((clone) => clone.remove());
        row.dataset.cloned = 'false';
      });
    }

    /**
     * Pause animation when the section scrolls out of the viewport.
     */
    setupIntersectionObserver() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.rows.forEach((row) => {
              if (entry.isIntersecting) {
                row.style.animationPlayState = 'running';
              } else {
                row.style.animationPlayState = 'paused';
              }
            });
          });
        },
        { threshold: 0 }
      );

      this.observer.observe(this);
    }

    /**
     * Pause on touch for mobile/tablet swiping.
     */
    setupTouchEvents() {
      this.rows.forEach((row) => {
        row.addEventListener('touchstart', () => {
          row.classList.add('tm-paused');
        }, { passive: true });

        row.addEventListener('touchend', () => {
          row.classList.remove('tm-paused');
        }, { passive: true });
      });
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      if (this.mediaQuery && this.handleBreakpoint) {
        this.mediaQuery.removeEventListener('change', this.handleBreakpoint);
      }
    }
  }

  customElements.define('aarka-testimonial-marquee', AarkaTestimonialMarquee);
}
